import React, { useContext, useEffect, useId } from 'react'
import {
   tokens,
   makeStyles,
   Toast,
   Toaster,
   useToastController,
   ToastBody,
   ToastTitle,
   ToastIntent,
   mergeClasses,
   TabList,
   SelectTabData,
   ProgressBar,
   Text,
} from '@fluentui/react-components'
import { Header } from '../components/common/header/Header'
import { Loader } from '../components/common/spinner/Loader'
import { t } from '../i18n/i18n'
import { IBreadcrumbInfo } from '../interfaces/IBreadcrumbInfo'
import { DataContext } from '../context/DataContext'
import { Navigation } from '../components/common/navigation/Navigation'
import { NavigationLink } from '../interfaces/INavigationLink'
import { BreadCrumbHeader } from '../components/common/header/BreadCrumbHeader'
import { useSearchParams } from 'react-router-dom'
type LayoutProps = {
   children: JSX.Element[] | JSX.Element | string | string[]
   breadCrumbItems: IBreadcrumbInfo[]
   activeDesktopLink: NavigationLink
   activePhoneTab?: string
   mainClassname?: string
   title?: string
   tabs?: JSX.Element
   button?: JSX.Element
   phoneNavLinks?: JSX.Element[] | JSX.Element
   customOnTabSelect?: (e: any, data: SelectTabData) => void
   overflowAuto?: boolean
   hideTabsInPhone?: boolean
}

const useStyles = makeStyles({
   layout: {
      height: '100vh',
      overflow: 'hidden',
   },
   main: {
      marginLeft: '64px',
      paddingTop: '60px',
      backgroundColor: tokens.colorNeutralBackground4,
      height: 'calc(100vh - 60px)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      '@media screen and (max-width: 1007px)': {
         height: 'calc(100vh - 100px)',
         paddingBottom: '80px',
         marginLeft: 0,
         overflow: 'auto',
      },
   },
   mainFullHeight: {
      '@media screen and (max-width: 1007px)': {
         height: 'calc(100vh - 60px)',
      },
   },
   contentHeader: {
      position: 'sticky',
      top: 0,
      zIndex: 2,
      display: 'flex',
      justifyContent: 'space-between',
      backgroundColor: tokens.colorNeutralBackground4,
      paddingTop: tokens.spacingHorizontalL,
      paddingBottom: tokens.spacingHorizontalL,
      '@media screen and (max-width: 640px)': {
         flexDirection: 'column',
         justifyItems: 'flex-start',
         gap: tokens.spacingVerticalXL,
      },
   },
   contentWrapper: {
      position: 'relative',
      height: '100%',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalXXXL,
      boxSizing: 'border-box',
      paddingBottom: tokens.spacingVerticalL,
      '@media screen and (max-width: 1007px)': {
         gap: tokens.spacingVerticalL,
         overflow: 'auto',
      },
   },
   contentSidePadding: {
      paddingLeft: tokens.spacingHorizontalL,
      paddingRight: tokens.spacingHorizontalL,
   },
   article: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxSizing: 'border-box',
      '@media screen and (max-width: 1007px)': {
         overflow: 'auto',
      },
   },
   showOnlyPhone: {
      '@media screen and (max-width: 1007px)': { display: 'none' },
   },
   overFlowAuto: {
      overflow: 'auto',
   },
   hideInPhone: {
      '@media screen and (max-width: 1007px)': {
         display: 'none',
      },
   },
   paddingTop: {
      paddingTop: tokens.spacingVerticalL,
   },
   initialLoader: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      gap: tokens.spacingVerticalM,
   },
   initialLoaderBar: {
      width: '200px',
   },
})

export const Layout = ({
   children,
   breadCrumbItems,
   mainClassname,
   title,
   tabs,
   button,
   activeDesktopLink,
   phoneNavLinks,
   customOnTabSelect,
   activePhoneTab,
   overflowAuto,
   hideTabsInPhone,
}: LayoutProps) => {
   const classes = useStyles()
   const toasterId = useId()
   const { dispatchToast } = useToastController(toasterId)
   const dataContext = useContext(DataContext)
   const { message, initialLoading } = dataContext.state
   const [searchParams, setSearchParams] = useSearchParams()
   const selectedTab = searchParams.get('tab')
   const notify = (intent: ToastIntent, title: string, text: string) => {
      dispatchToast(
         <Toast>
            <ToastTitle>{title}</ToastTitle>
            {text && <ToastBody>{text}</ToastBody>}
         </Toast>,
         { intent }
      )
   }
   useEffect(() => {
      if (message) {
         notify(message.intent, message.title, message?.text)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [message])

   return (
      <div className={mergeClasses(classes.layout, overflowAuto ? classes.overFlowAuto : '')}>
         <Header />
         <main className={mergeClasses(classes.main, overflowAuto ? classes.overFlowAuto : '', !phoneNavLinks ? classes.mainFullHeight : '')}>
            <BreadCrumbHeader breadCrumbItems={breadCrumbItems} title={title} />
            <Navigation
               activeDesktopLink={activeDesktopLink}
               phoneNavLinks={phoneNavLinks}
               customOnTabSelect={customOnTabSelect}
               activePhoneTab={activePhoneTab}
            />
            <article className={mergeClasses(classes.article, overflowAuto ? classes.overFlowAuto : '')}>
               {tabs || button ? (
                  <header
                     className={mergeClasses(
                        classes.contentSidePadding,
                        classes.contentHeader,
                        hideTabsInPhone ? classes.hideInPhone : '',
                        phoneNavLinks ? classes.showOnlyPhone : ''
                     )}
                  >
                     <TabList selectedValue={selectedTab} onTabSelect={(e, data) => setSearchParams({ tab: data.value as string })}>
                        {tabs}
                     </TabList>
                     {button}
                  </header>
               ) : null}

               <div
                  className={mergeClasses(
                     classes.contentSidePadding,
                     classes.contentWrapper,
                     !tabs && !button ? classes.paddingTop : '',
                     overflowAuto ? classes.overFlowAuto : '',
                     mainClassname ?? ''
                  )}
               >
                  {initialLoading ? (
                     <div className={classes.initialLoader}>
                        <Text size={300} weight="semibold">{t('Loading')}</Text>
                        <ProgressBar className={classes.initialLoaderBar} />
                     </div>
                  ) : (
                     <>{children}</>
                  )}
               </div>
            </article>
         </main>
         <Toaster pauseOnHover position="top-end" toasterId={toasterId} />
      </div>
   )
}
